import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { useDovizKurlari } from '@/hooks/useDovizKurlari';
import type { Rezervasyon, Driver, DriverPayment } from '@/types/rezervasyon';

interface DriverSummary {
  driver: Driver;
  toplamIs: number;
  isler: Rezervasyon[];
  pbOzet: Record<string, { isGeliri: number; odemeler: number; maas: number; net: number }>;
}

const PARA_BIRIMLERI = ['TL', 'EUR', 'USD', 'GBP'];

function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

function getMonthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function SoforHesapPage() {
  useAuthGuard();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [rezervasyonlar, setRezervasyonlar] = useState<Rezervasyon[]>([]);
  const [driverPayments, setDriverPayments] = useState<DriverPayment[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');

  const [seciliSofor, setSeciliSofor] = useState<DriverSummary | null>(null);
  const [hedefPB, setHedefPB] = useState('TL');
  const [tarihBas, setTarihBas] = useState('');
  const [tarihBit, setTarihBit] = useState('');
  const [donemBtn, setDonemBtn] = useState('');
  const [odemeYukleniyor, setOdemeYukleniyor] = useState<number | null>(null);

  const [showAddPayment, setShowAddPayment] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [yeniOdeme, setYeniOdeme] = useState({ tarih: getTodayStr(), tutar: '', para_birimi: 'EUR', aciklama: '', odeme_turu: 'avans' });

  const { kurlar, yukleniyor: kurlarYukleniyor, hata: kurlarHata, cevir, format } = useDovizKurlari();

  const fetchData = useCallback(async () => {
    setYukleniyor(true);
    const [dRes, rRes, pRes] = await Promise.all([
      supabase.from('drivers').select('*').eq('aktif', true).order('ad_soyad'),
      supabase.from('reservations').select('*').order('gelis_tarihi', { ascending: false }),
      supabase.from('driver_payments').select('*').order('tarih', { ascending: false }),
    ]);
    if (dRes.error) setHata(dRes.error.message);
    else setDrivers(dRes.data || []);
    if (rRes.error) setHata(rRes.error.message);
    else setRezervasyonlar((rRes.data as Rezervasyon[]) || []);
    if (pRes.error) setHata(pRes.error.message);
    else setDriverPayments((pRes.data as DriverPayment[]) || []);
    setYukleniyor(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summaries = useMemo(() => {
    const list = drivers.map(d => {
      const isler = rezervasyonlar.filter(r => r.sofor?.trim().toLowerCase() === d.ad_soyad.trim().toLowerCase() && r.durum !== 'İptal Edildi');
      const soforPayments = driverPayments.filter(p => p.driver_id === d.id);
      const pbOzet: Record<string, { isGeliri: number; odemeler: number; maas: number; net: number }> = {};
      PARA_BIRIMLERI.forEach(pb => {
        const isGeliri = isler
          .filter(r => r.para_birimi === pb)
          .reduce((s, r) => s + (Number(r.ucret) || 0), 0);
        const odemeler = soforPayments
          .filter(p => p.para_birimi === pb)
          .reduce((s, p) => s + (Number(p.tutar) || 0), 0);
        const maas = cevir(Number(d.maas) || 0, d.maas_para_birimi, pb);
        pbOzet[pb] = { isGeliri, odemeler, maas, net: isGeliri + maas - odemeler };
      });
      return {
        driver: d,
        toplamIs: isler.length,
        isler,
        pbOzet,
      };
    });
    return list.sort((a, b) => b.pbOzet['TL'].net - a.pbOzet['TL'].net);
  }, [drivers, rezervasyonlar, driverPayments, cevir]);

  const setHizliDonem = (tip: string) => {
    const today = getTodayStr();
    if (tip === 'bugun') {
      setTarihBas(today); setTarihBit(today);
    } else if (tip === 'ay') {
      setTarihBas(getMonthStartStr()); setTarihBit(today);
    } else {
      setTarihBas(''); setTarihBit('');
    }
    setDonemBtn(tip);
  };

  const filteredIsler = useMemo(() => {
    if (!seciliSofor) return [];
    let list = [...seciliSofor.isler];
    if (tarihBas) list = list.filter(r => r.gelis_tarihi >= tarihBas);
    if (tarihBit) list = list.filter(r => r.gelis_tarihi <= tarihBit);
    return list;
  }, [seciliSofor, tarihBas, tarihBit]);

  const filteredPayments = useMemo(() => {
    if (!seciliSofor) return [];
    let list = driverPayments.filter(p => p.driver_id === seciliSofor.driver.id);
    if (tarihBas) list = list.filter(p => p.tarih >= tarihBas);
    if (tarihBit) list = list.filter(p => p.tarih <= tarihBit);
    return list;
  }, [seciliSofor, driverPayments, tarihBas, tarihBit]);

  const modalPbOzet = useMemo(() => {
    if (!seciliSofor) return {} as Record<string, { isGeliri: number; odemeler: number; maas: number; net: number }>;
    const ozet: Record<string, { isGeliri: number; odemeler: number; maas: number; net: number }> = {};
    PARA_BIRIMLERI.forEach(pb => {
      const isGeliri = filteredIsler
        .filter(r => r.para_birimi === pb)
        .reduce((s, r) => s + (Number(r.ucret) || 0), 0);
      const odemeler = filteredPayments
        .filter(p => p.para_birimi === pb)
        .reduce((s, p) => s + (Number(p.tutar) || 0), 0);
      const maas = cevir(Number(seciliSofor.driver.maas) || 0, seciliSofor.driver.maas_para_birimi, pb);
      ozet[pb] = { isGeliri, odemeler, maas, net: isGeliri + maas - odemeler };
    });
    return ozet;
  }, [seciliSofor, filteredIsler, filteredPayments, cevir]);

  const odenenOzet = useMemo(() => {
    const odenen = filteredIsler.filter(r => r.sofor_odenme);
    const odenmemis = filteredIsler.filter(r => !r.sofor_odenme);
    const odenenPb: Record<string, number> = {};
    const odenmemisPb: Record<string, number> = {};
    PARA_BIRIMLERI.forEach(pb => {
      odenenPb[pb] = odenen
        .filter(r => r.para_birimi === pb)
        .reduce((s, r) => s + (Number(r.ucret) || 0), 0);
      odenmemisPb[pb] = odenmemis
        .filter(r => r.para_birimi === pb)
        .reduce((s, r) => s + (Number(r.ucret) || 0), 0);
    });
    return { odenen: odenen.length, odenmemis: odenmemis.length, odenenPb, odenmemisPb };
  }, [filteredIsler]);

  const toplamIsGeliri = useMemo(() => {
    const pb: Record<string, number> = {};
    PARA_BIRIMLERI.forEach(p => {
      pb[p] = filteredIsler
        .filter(r => r.para_birimi === p)
        .reduce((s, r) => s + (Number(r.ucret) || 0), 0);
    });
    return pb;
  }, [filteredIsler]);

  const tumOdemelerOzet = useMemo(() => {
    const ozet: Record<string, { toplam: number; avans: number; maas: number; diger: number }> = {};
    PARA_BIRIMLERI.forEach(pb => {
      const pbOdemeler = driverPayments.filter(p => p.para_birimi === pb);
      ozet[pb] = {
        toplam: pbOdemeler.reduce((s, p) => s + (Number(p.tutar) || 0), 0),
        avans: pbOdemeler.filter(p => p.odeme_turu === 'avans').reduce((s, p) => s + (Number(p.tutar) || 0), 0),
        maas: pbOdemeler.filter(p => p.odeme_turu === 'maas').reduce((s, p) => s + (Number(p.tutar) || 0), 0),
        diger: pbOdemeler.filter(p => !['avans', 'maas'].includes(p.odeme_turu || '')).reduce((s, p) => s + (Number(p.tutar) || 0), 0),
      };
    });
    return ozet;
  }, [driverPayments]);

  const toplamOdenenIs = useMemo(() => {
    return rezervasyonlar.filter(r => r.sofor && r.sofor_odenme && r.durum !== 'İptal Edildi').length;
  }, [rezervasyonlar]);

  const toplamOdenmemisIs = useMemo(() => {
    return rezervasyonlar.filter(r => r.sofor && !r.sofor_odenme && r.durum !== 'İptal Edildi').length;
  }, [rezervasyonlar]);

  const togglePayment = async (id: number, mevcut: boolean) => {
    setOdemeYukleniyor(id);
    const { data, error } = await supabase.functions.invoke('manage-reservations', {
      body: { action: 'updatePayment', id, data: { sofor_odenme: !mevcut } },
    });
    if (error || !data?.success) {
      setHata('Ödeme durumu güncellenemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
    } else {
      setRezervasyonlar(prev => prev.map(r => r.id === id ? { ...r, sofor_odenme: !mevcut } : r));
      if (seciliSofor) {
        setSeciliSofor({
          ...seciliSofor,
          isler: seciliSofor.isler.map(r => r.id === id ? { ...r, sofor_odenme: !mevcut } : r),
        });
      }
    }
    setOdemeYukleniyor(null);
  };

  const handleAddPayment = async () => {
    if (!seciliSofor || !yeniOdeme.tutar || parseFloat(yeniOdeme.tutar) <= 0) return;
    setAddingPayment(true);
    setHata('');
    const { data, error: err } = await supabase.functions.invoke('manage-data', {
      body: {
        action: 'insert',
        table: 'driver_payments',
        data: {
          driver_id: seciliSofor.driver.id,
          tarih: yeniOdeme.tarih,
          tutar: parseFloat(yeniOdeme.tutar),
          para_birimi: yeniOdeme.para_birimi,
          aciklama: yeniOdeme.aciklama,
          odeme_turu: yeniOdeme.odeme_turu,
        },
      },
    });
    if (err || !data?.success) {
      setHata('Ödeme kaydedilemedi: ' + (err?.message || data?.error || 'Bilinmeyen hata'));
    } else {
      setDriverPayments(prev => [data.data, ...prev]);
      setYeniOdeme({ tarih: getTodayStr(), tutar: '', para_birimi: 'EUR', aciklama: '', odeme_turu: 'avans' });
      setShowAddPayment(false);
    }
    setAddingPayment(false);
  };

  const handleDeletePayment = async (id: number) => {
    setHata('');
    const { data, error } = await supabase.functions.invoke('manage-data', {
      body: { action: 'delete', table: 'driver_payments', id },
    });
    if (error || !data?.success) {
      setHata('Ödeme silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
    } else {
      setDriverPayments(prev => prev.filter(p => p.id !== id));
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-display font-bold text-white">Şoför Hesap</h2>
        <p className="text-sm text-gray-400 mt-1">
          {yukleniyor ? 'Yükleniyor...' : `${summaries.length} aktif şoför · ${rezervasyonlar.filter(r => r.sofor && r.durum !== 'İptal Edildi').length} atanmış iş`}
        </p>
      </div>

      {hata && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{hata}</div>}
      {kurlarHata && <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-yellow-400"><i className="ri-alert-line mr-2"></i>Döviz kurları alınamadı: {kurlarHata}</div>}

      {/* Döviz Bilgisi */}
      {kurlar && (
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-gray-400">
          <span className="bg-vip-dark border border-vip-gold/10 rounded-md px-3 py-1.5">
            <i className="ri-exchange-line text-vip-gold mr-1"></i>
            EUR &rarr; {kurlar.rates.TRY ? `₺${kurlar.rates.TRY.toFixed(2)}` : '-'} ·
            $ {kurlar.rates.USD ? `${kurlar.rates.USD.toFixed(2)}` : '-'} ·
            £ {kurlar.rates.GBP ? `${kurlar.rates.GBP.toFixed(2)}` : '-'}
          </span>
          <span className="text-gray-500">{kurlar.tarih}</span>
        </div>
      )}

      {/* Şoför Kartları */}
      {yukleniyor ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 animate-pulse">
              <div className="h-4 bg-white/5 rounded w-3/4 mb-3"></div>
              <div className="h-8 bg-white/5 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-white/5 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {summaries.map((s) => (
            <button
              key={s.driver.id}
              onClick={() => { setSeciliSofor(s); setDonemBtn(''); setTarihBas(''); setTarihBit(''); }}
              className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 hover:border-vip-gold/30 transition-all text-left group cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center text-vip-gold font-semibold text-sm flex-shrink-0">
                  {s.driver.ad_soyad.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate group-hover:text-vip-gold transition-colors">{s.driver.ad_soyad}</p>
                  <p className="text-xs text-gray-500">{s.driver.telefon}</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Toplam İş</p>
                  <p className="text-lg font-bold text-white">{s.toplamIs}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {PARA_BIRIMLERI.map(pb => {
                  const net = s.pbOzet[pb]?.net ?? 0;
                  if (Math.abs(net) < 0.01) return null;
                  return (
                    <span key={pb} className={`text-[10px] px-2 py-1 rounded border ${net > 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                      {format(Math.abs(net), pb)} {net > 0 ? '(alınacak)' : '(borç)'}
                    </span>
                  );
                })}
              </div>
              {s.driver.maas > 0 && (
                <p className="text-[10px] text-gray-500 mt-2">
                  <i className="ri-wallet-3-line mr-1"></i>Maaş: {s.driver.maas} {s.driver.maas_para_birimi}
                </p>
              )}
              {s.toplamIs > 0 && (
                <p className="text-[10px] text-gray-500 mt-2">
                  <i className="ri-arrow-right-line mr-1"></i>Detaylı görmek için tıklayın
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Tüm Ödemeler Genel Özet */}
      {!yukleniyor && summaries.length > 0 && (
        <div className="mt-6 bg-vip-dark border border-vip-gold/10 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-vip-gold/10 flex items-center gap-2">
            <i className="ri-bank-line text-vip-gold"></i>
            <h3 className="text-sm font-semibold text-white">Tüm Şoförlere Yapılan Ödemeler</h3>
            <span className="text-[10px] text-gray-500 ml-auto">
              {driverPayments.length} işlem · {toplamOdenenIs} iş ödendi · {toplamOdenmemisIs} iş bekliyor
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-vip-gold/10">
                  <th className="text-left py-2.5 px-4 text-gray-500 font-medium">Para Birimi</th>
                  <th className="text-right py-2.5 px-4 text-gray-500 font-medium">Avans</th>
                  <th className="text-right py-2.5 px-4 text-gray-500 font-medium">Maaş Ödemesi</th>
                  <th className="text-right py-2.5 px-4 text-gray-500 font-medium">Diğer</th>
                  <th className="text-right py-2.5 px-4 text-gray-500 font-medium">Genel Toplam</th>
                </tr>
              </thead>
              <tbody>
                {PARA_BIRIMLERI.map(pb => {
                  const o = tumOdemelerOzet[pb];
                  if (!o || Math.abs(o.toplam) < 0.01) return null;
                  return (
                    <tr key={pb} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors">
                      <td className="py-2.5 px-4 font-semibold text-white">{pb}</td>
                      <td className="py-2.5 px-4 text-right text-amber-400">{format(o.avans, pb)}</td>
                      <td className="py-2.5 px-4 text-right text-blue-400">{format(o.maas, pb)}</td>
                      <td className="py-2.5 px-4 text-right text-gray-400">{format(o.diger, pb)}</td>
                      <td className="py-2.5 px-4 text-right font-bold text-vip-gold">{format(o.toplam, pb)}</td>
                    </tr>
                  );
                })}
                {driverPayments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-gray-500">
                      <i className="ri-inbox-line text-lg mb-1 block"></i>
                      Henüz hiçbir ödeme kaydı bulunmuyor.
                    </td>
                  </tr>
                )}
              </tbody>
              {driverPayments.length > 0 && (
                <tfoot>
                  <tr className="border-t border-vip-gold/10 bg-vip-black/30">
                    <td className="py-2.5 px-4 text-gray-400 font-medium">
                      <i className="ri-wallet-3-line mr-1"></i>Tüm Para Birimleri
                    </td>
                    <td className="py-2.5 px-4 text-right text-amber-400 font-bold">
                      {PARA_BIRIMLERI.map(pb => {
                        const t = tumOdemelerOzet[pb]?.avans ?? 0;
                        if (Math.abs(t) < 0.01) return null;
                        return <span key={pb} className="whitespace-nowrap mr-2">{format(t, pb)}</span>;
                      })}
                      {PARA_BIRIMLERI.every(pb => Math.abs(tumOdemelerOzet[pb]?.avans ?? 0) < 0.01) && <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-blue-400 font-bold">
                      {PARA_BIRIMLERI.map(pb => {
                        const t = tumOdemelerOzet[pb]?.maas ?? 0;
                        if (Math.abs(t) < 0.01) return null;
                        return <span key={pb} className="whitespace-nowrap mr-2">{format(t, pb)}</span>;
                      })}
                      {PARA_BIRIMLERI.every(pb => Math.abs(tumOdemelerOzet[pb]?.maas ?? 0) < 0.01) && <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-gray-400 font-bold">
                      {PARA_BIRIMLERI.map(pb => {
                        const t = tumOdemelerOzet[pb]?.diger ?? 0;
                        if (Math.abs(t) < 0.01) return null;
                        return <span key={pb} className="whitespace-nowrap mr-2">{format(t, pb)}</span>;
                      })}
                      {PARA_BIRIMLERI.every(pb => Math.abs(tumOdemelerOzet[pb]?.diger ?? 0) < 0.01) && <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-vip-gold font-bold text-sm">
                      {PARA_BIRIMLERI.map(pb => {
                        const t = tumOdemelerOzet[pb]?.toplam ?? 0;
                        if (Math.abs(t) < 0.01) return null;
                        return <span key={pb} className="whitespace-nowrap mr-2">{format(t, pb)}</span>;
                      })}
                      {driverPayments.length === 0 && <span className="text-gray-500">—</span>}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {summaries.length === 0 && !yukleniyor && (
        <div className="text-center py-12 text-gray-500">
          <i className="ri-steering-line text-4xl mb-3 block"></i>
          <p className="text-sm">Aktif şoför bulunamadı.</p>
          <p className="text-xs mt-1">Ayarlar &gt; Şoförler bölümünden yeni şoför ekleyebilirsiniz.</p>
        </div>
      )}

      {/* Detay Modal */}
      {seciliSofor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-vip-dark border border-vip-gold/20 rounded-xl w-full max-w-5xl mt-8 mb-8 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-vip-gold/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center text-vip-gold font-semibold text-sm flex-shrink-0">
                  {seciliSofor.driver.ad_soyad.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">{seciliSofor.driver.ad_soyad}</h3>
                  <p className="text-xs text-gray-500">{seciliSofor.driver.telefon} {seciliSofor.driver.lisans_no && `· Lisans: ${seciliSofor.driver.lisans_no}`} {seciliSofor.driver.maas > 0 && `· Maaş: ${seciliSofor.driver.maas} ${seciliSofor.driver.maas_para_birimi}`}</p>
                </div>
              </div>
              <button onClick={() => setSeciliSofor(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Controls */}
            <div className="p-5 border-b border-vip-gold/10 space-y-3">
              {/* Period + Currency */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Quick Period */}
                <div className="flex gap-1 bg-vip-black border border-vip-gold/10 rounded-lg p-1">
                  {[
                    { key: 'tumu', label: 'Tümü' },
                    { key: 'bugun', label: 'Bugün' },
                    { key: 'ay', label: 'Bu Ay' },
                  ].map(({ key, label }) => (
                    <button key={key} onClick={() => setHizliDonem(key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ${donemBtn === key ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Date Range */}
                <div className="flex items-center gap-2">
                  <input type="date" value={tarihBas} onChange={e => { setTarihBas(e.target.value); setDonemBtn(''); }}
                    className="bg-vip-black border border-vip-gold/15 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:border-vip-gold/40" />
                  <span className="text-gray-500 text-xs">—</span>
                  <input type="date" value={tarihBit} onChange={e => { setTarihBit(e.target.value); setDonemBtn(''); }}
                    className="bg-vip-black border border-vip-gold/15 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:border-vip-gold/40" />
                </div>
                {/* Currency Selector */}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-gray-500">Para Birimi:</span>
                  <div className="flex gap-1 bg-vip-black border border-vip-gold/10 rounded-lg p-1">
                    {PARA_BIRIMLERI.map(pb => (
                      <button key={pb} onClick={() => setHedefPB(pb)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${hedefPB === pb ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
                        {pb}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Financial Summary Table — per currency */}
              <div className="bg-vip-black/50 rounded-lg border border-vip-gold/10 overflow-hidden">
                <div className="px-3 py-2 border-b border-vip-gold/10">
                  <p className="text-xs font-semibold text-gray-400">
                    <i className="ri-calculator-line mr-1"></i>Hesap Özeti (Tüm Para Birimleri)
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-vip-gold/10">
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Para Birimi</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">İş Geliri</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Maaş</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Ödemeler</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Net Kalan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PARA_BIRIMLERI.map(pb => {
                        const ozet = modalPbOzet[pb];
                        if (!ozet || (Math.abs(ozet.isGeliri) < 0.01 && Math.abs(ozet.maas) < 0.01 && Math.abs(ozet.odemeler) < 0.01)) return null;
                        return (
                          <tr key={pb} className="border-b border-vip-gold/5">
                            <td className="py-2 px-3 font-medium text-white">{pb}</td>
                            <td className="py-2 px-3 text-right text-vip-gold">{format(ozet.isGeliri, pb)}</td>
                            <td className="py-2 px-3 text-right text-gray-300">{format(ozet.maas, pb)}</td>
                            <td className="py-2 px-3 text-right text-amber-400">{format(ozet.odemeler, pb)}</td>
                            <td className={`py-2 px-3 text-right font-bold ${ozet.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {format(Math.abs(ozet.net), pb)}
                              {ozet.net >= 0 ? <span className="text-[10px] font-normal ml-1 text-gray-500">(alınacak)</span> : <span className="text-[10px] font-normal ml-1 text-gray-500">(borç)</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ödenen / Ödenmemiş Özet */}
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
                      <i className="ri-check-double-line text-emerald-400 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Ödenen İş</p>
                      <p className="text-sm font-bold text-emerald-400">{odenenOzet.odenen} iş</p>
                    </div>
                  </div>
                  <div className="text-right flex flex-wrap gap-1">
                    {PARA_BIRIMLERI.map(pb => {
                      const tutar = odenenOzet.odenenPb[pb] ?? 0;
                      if (Math.abs(tutar) < 0.01) return null;
                      return (
                        <span key={pb} className="text-xs font-bold text-emerald-400 whitespace-nowrap">
                          {format(tutar, pb)}
                        </span>
                      );
                    })}
                    {odenenOzet.odenen === 0 && <span className="text-xs font-bold text-emerald-400">0</span>}
                  </div>
                </div>
                <div className="flex-1 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-amber-500/15 flex items-center justify-center">
                      <i className="ri-time-line text-amber-400 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Ödenmemiş İş</p>
                      <p className="text-sm font-bold text-amber-400">{odenenOzet.odenmemis} iş</p>
                    </div>
                  </div>
                  <div className="text-right flex flex-wrap gap-1">
                    {PARA_BIRIMLERI.map(pb => {
                      const tutar = odenenOzet.odenmemisPb[pb] ?? 0;
                      if (Math.abs(tutar) < 0.01) return null;
                      return (
                        <span key={pb} className="text-xs font-bold text-amber-400 whitespace-nowrap">
                          {format(tutar, pb)}
                        </span>
                      );
                    })}
                    {odenenOzet.odenmemis === 0 && <span className="text-xs font-bold text-amber-400">0</span>}
                  </div>
                </div>
              </div>

              {/* Add Payment Button */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  <i className="ri-information-line mr-1"></i>
                  Net kalan = Maaş + İş geliri - Yapılan ödemeler
                </p>
                <button onClick={() => setShowAddPayment(!showAddPayment)}
                  className="px-3 py-1.5 bg-amber-500/15 text-amber-400 text-xs font-medium rounded-md border border-amber-500/20 hover:bg-amber-500/25 transition-colors whitespace-nowrap">
                  <i className="ri-add-line mr-1"></i>Ödeme / Avans Ekle
                </button>
              </div>

              {/* Add Payment Form */}
              {showAddPayment && (
                <div className="bg-vip-black/50 border border-amber-500/20 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-medium text-amber-400">Yeni Ödeme / Avans Ekle</p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Tarih</label>
                      <input type="date" value={yeniOdeme.tarih} onChange={e => setYeniOdeme(p => ({ ...p, tarih: e.target.value }))}
                        className="w-full bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/40" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Tutar</label>
                      <input type="number" value={yeniOdeme.tutar} onChange={e => setYeniOdeme(p => ({ ...p, tutar: e.target.value }))}
                        placeholder="0.00" className="w-full bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/40" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Para Birimi</label>
                      <select value={yeniOdeme.para_birimi} onChange={e => setYeniOdeme(p => ({ ...p, para_birimi: e.target.value }))}
                        className="w-full bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/40">
                        {PARA_BIRIMLERI.map(pb => <option key={pb} value={pb}>{pb}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Tür</label>
                      <select value={yeniOdeme.odeme_turu} onChange={e => setYeniOdeme(p => ({ ...p, odeme_turu: e.target.value }))}
                        className="w-full bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/40">
                        <option value="avans">Avans</option>
                        <option value="maas">Maaş Ödemesi</option>
                        <option value="diger">Diğer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Açıklama</label>
                      <input value={yeniOdeme.aciklama} onChange={e => setYeniOdeme(p => ({ ...p, aciklama: e.target.value }))}
                        placeholder="Açıklama..." className="w-full bg-vip-dark border border-vip-gold/15 rounded-md px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/40" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddPayment(false)} className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors whitespace-nowrap">İptal</button>
                    <button onClick={handleAddPayment} disabled={addingPayment || !yeniOdeme.tutar || parseFloat(yeniOdeme.tutar) <= 0}
                      className="px-4 py-1.5 bg-amber-500 text-vip-black text-xs font-semibold rounded-md hover:bg-amber-400 transition-colors disabled:opacity-50 whitespace-nowrap">
                      {addingPayment ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Payments Table */}
            {filteredPayments.length > 0 && (
              <div className="px-5 pb-3">
                <p className="text-xs font-semibold text-gray-400 mb-2">
                  <i className="ri-wallet-3-line mr-1"></i>Yapılan Ödemeler / Avanslar ({filteredPayments.length})
                </p>
                <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-vip-dark z-10">
                      <tr className="border-b border-vip-gold/10">
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Tarih</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Tür</th>
                        <th className="text-left py-2 px-3 text-gray-400 font-medium">Açıklama</th>
                        <th className="text-right py-2 px-3 text-gray-400 font-medium">Tutar</th>
                        <th className="text-center py-2 px-3 text-gray-400 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map(p => (
                        <tr key={p.id} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors">
                          <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{p.tarih}</td>
                          <td className="py-2 px-3">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${p.odeme_turu === 'avans' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : p.odeme_turu === 'maas' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
                              {p.odeme_turu === 'avans' ? 'Avans' : p.odeme_turu === 'maas' ? 'Maaş' : 'Diğer'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-gray-400">{p.aciklama || '-'}</td>
                          <td className="py-2 px-3 text-right font-semibold text-amber-400">
                            {format(Number(p.tutar) || 0, p.para_birimi)}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button onClick={() => handleDeletePayment(p.id)}
                              className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                              title="Sil">
                              <i className="ri-delete-bin-line text-[10px]"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Jobs Table */}
            <div className="p-5">
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-vip-dark z-10">
                    <tr className="border-b border-vip-gold/10">
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Tarih</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rez. No</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Müşteri</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rota</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Araç / Plaka</th>
                      <th className="text-right py-2.5 px-3 text-gray-400 font-medium">Orjinal</th>
                      <th className="text-right py-2.5 px-3 text-gray-400 font-medium">{hedefPB}</th>
                      <th className="text-center py-2.5 px-3 text-gray-400 font-medium">Durum</th>
                      <th className="text-center py-2.5 px-3 text-gray-400 font-medium">Ödeme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIsler.map(r => {
                      const cevrilmis = cevir(Number(r.ucret) || 0, r.para_birimi, hedefPB);
                      const odenmis = !!r.sofor_odenme;
                      return (
                        <tr key={r.id} className={`border-b border-vip-gold/5 hover:bg-white/5 transition-colors ${odenmis ? 'opacity-50' : ''}`}>
                          <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">{r.gelis_tarihi}</td>
                          <td className={`py-2.5 px-3 font-mono ${odenmis ? 'text-gray-500 line-through' : 'text-vip-gold'}`}>{r.rezervasyon_no}</td>
                          <td className={`py-2.5 px-3 ${odenmis ? 'text-gray-500' : 'text-white'}`}>{r.musteri_adi}</td>
                          <td className={`py-2.5 px-3 ${odenmis ? 'text-gray-500' : 'text-gray-400'}`}>{r.alinis_yeri} {r.birak_yeri !== 'Yok' && r.birak_yeri ? `→ ${r.birak_yeri}` : ''}</td>
                          <td className={`py-2.5 px-3 ${odenmis ? 'text-gray-500' : 'text-gray-400'}`}>{r.atanan_arac || '-'} {r.atanan_plaka ? `(${r.atanan_plaka})` : ''}</td>
                          <td className={`py-2.5 px-3 text-right ${odenmis ? 'text-gray-500' : 'text-gray-400'}`}>{r.ucret} {r.para_birimi}</td>
                          <td className={`py-2.5 px-3 text-right font-semibold ${odenmis ? 'text-gray-500 line-through' : 'text-vip-gold'}`}>{format(cevrilmis, hedefPB)}</td>
                          <td className="py-2.5 px-3 text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${r.durum === 'Transfer Tamamlandı' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : r.durum === 'İptal Edildi' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-vip-gold/10 text-vip-gold border-vip-gold/20'}`}>
                              {r.durum}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              onClick={() => togglePayment(r.id, odenmis)}
                              disabled={odemeYukleniyor === r.id}
                              className={`w-6 h-6 rounded border flex items-center justify-center transition-all cursor-pointer ${
                                odenmis
                                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/30'
                                  : 'bg-transparent border-gray-600 hover:border-vip-gold/40 hover:bg-vip-gold/10'
                              }`}
                              title={odenmis ? 'Ödeme yapıldı (geri al)' : 'Ödeme yapıldı işaretle'}
                            >
                              {odemeYukleniyor === r.id ? (
                                <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                              ) : odenmis ? (
                                <i className="ri-check-line text-[10px]"></i>
                              ) : (
                                <i className="ri-check-line text-[10px] text-transparent"></i>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredIsler.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-gray-500 text-sm">
                          <i className="ri-inbox-line text-2xl mb-2 block"></i>
                          Seçili dönemde iş kaydı bulunamadı.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-vip-dark z-10">
                    <tr className="border-t border-vip-gold/20">
                      <td colSpan={6} className="py-3 px-3 text-right text-xs text-gray-400 font-medium">TOPLAM</td>
                      <td className="py-3 px-3 text-right text-vip-gold font-bold text-sm">
                        {PARA_BIRIMLERI.map(pb => {
                          const tutar = toplamIsGeliri[pb] ?? 0;
                          if (Math.abs(tutar) < 0.01) return null;
                          return (
                            <span key={pb} className="whitespace-nowrap mr-2">
                              {format(tutar, pb)}
                            </span>
                          );
                        })}
                      </td>
                      <td className="py-3 px-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t border-vip-gold/10">
              <div className="text-[10px] text-gray-500">
                {kurlarYukleniyor ? 'Kurlar yükleniyor...' : kurlar ? `Kur: ${kurlar.tarih}` : 'Kur verisi yok'}
              </div>
              <button onClick={() => setSeciliSofor(null)} className="px-4 py-2 bg-vip-gold/15 text-vip-gold text-xs font-medium rounded-md border border-vip-gold/20 hover:bg-vip-gold/25 transition-colors whitespace-nowrap">
                <i className="ri-close-line mr-1"></i>Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}